import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiCheckCircle, FiCloud, FiSearch, FiShield } from "react-icons/fi";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import "./BusinessUnitProviders.css";

const API = "/api/admin/business-unit-providers";
const AWS_FIELDS = ["account_id", "region", "role_arn", "external_id", "bucket", "kms_key_id", "public_base_url"];
const normalizeSearch = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Whitelist response fields: credentials must never become readable form data.
function readConfiguration(data) {
  return {
    routing_enabled: data?.routing_enabled === true,
    aws: Object.fromEntries([
      ["mode", data?.aws?.mode === "business_unit" ? "business_unit" : "central"],
      ...AWS_FIELDS.map((key) => [key, String(data?.aws?.[key] || "")]),
    ]),
    openai: {
      mode: data?.openai?.mode === "business_unit" ? "business_unit" : "central",
      project_id: String(data?.openai?.project_id || ""),
      has_key: data?.openai?.has_key === true,
    },
  };
}

function errorMessage(error, fallback) {
  const providerErrors = {
    BU_PROVIDER_CENTRAL_ACCOUNT: "Esta cuenta AWS es la central de Pilcore. Indica una cuenta miembro propia de esta BU.",
    BU_PROVIDER_CENTRAL_BUCKET: "Este bucket pertenece al flujo centralizado. Crea un bucket exclusivo para esta BU.",
    BU_PROVIDER_CENTRAL_PROJECT: "Este proyecto OpenAI es el central. Crea un proyecto exclusivo para esta BU.",
    BU_PROVIDER_IDENTITY_CLAIMED: "La cuenta AWS o el proyecto OpenAI ya está asignado a otra BU. Usa uno exclusivo para esta BU.",
    BU_PROVIDER_BUCKET_CLAIMED: "Este bucket ya está asignado a otra BU. Usa un bucket exclusivo para esta BU.",
    BU_PROVIDER_UNAVAILABLE: "La configuración no está disponible. Revisa la instalación del servidor, los accesos y la configuración guardada.",
    BU_PROVIDER_CHECK_FAILED: "No se pudo verificar la conexión. Revisa los accesos guardados, la cuenta, la región y los permisos del proveedor.",
  };
  const code = error?.response?.data?.code;
  if (Object.prototype.hasOwnProperty.call(providerErrors, code)) return providerErrors[code];
  const status = error?.response?.status;
  if (status === 403) return "No tienes permiso para realizar esta operación.";
  if (status === 401) return "Tu sesión ha caducado. Vuelve a iniciar sesión.";
  if (status === 404) return "La BU o su configuración ya no está disponible.";
  if (status === 409) return "Hay un conflicto con la configuración. Comprueba que la cuenta, el bucket y el proyecto sean exclusivos de esta BU.";
  if (status === 400 || status === 422) return "Revisa los datos: la cuenta, el rol, el bucket y el proyecto deben ser válidos y completos para preparar la vía por BU.";
  return fallback;
}

function ProviderMode({ provider, value, onChange, routingEnabled }) {
  return <fieldset className="buProvidersModes">
    <legend>Vía de {provider}</legend>
    <label className={value === "central" ? "isSelected" : ""}>
      <input type="radio" name={`${provider}-mode`} value="central" checked={value === "central"} onChange={() => onChange("central")} />
      <span><strong>Centralizada</strong><small>Usar la configuración actual de Pilcore.</small></span>
    </label>
    <label className={value === "business_unit" ? "isSelected" : ""}>
      <input type="radio" name={`${provider}-mode`} value="business_unit" checked={value === "business_unit"} onChange={() => onChange("business_unit")} />
      <span><strong>{routingEnabled ? "Por BU" : "Preparar por BU"}</strong><small>{routingEnabled ? "Usar los accesos propios en los flujos habilitados." : "Guardar los accesos para la activación posterior."}</small></span>
    </label>
  </fieldset>;
}

function CheckResult({ provider, result }) {
  if (!result) return null;
  return <div className={`buProvidersCheck ${result.success ? "isSuccess" : "isError"}`} role={result.success ? "status" : "alert"}>
    <strong>{result.success ? `Comprobación de ${provider} completada` : `No se pudo verificar ${provider}`}</strong>
    {result.configuration && <p>{result.configuration === "business_unit" ? "Se han comprobado los accesos guardados de esta BU." : "Se ha comprobado la configuración central de Pilcore."}</p>}
    {result.message && <p>{result.message}</p>}
    {result.checks?.length > 0 && <ul>{result.checks.map((check, index) => <li key={index}>{check}</li>)}</ul>}
    {result.limitations?.length > 0 && <ul>{result.limitations.map((limit, index) => <li key={index}>{limit}</li>)}</ul>}
    {result.success && <p>Esta comprobación no activa la vía por BU ni confirma todos los permisos necesarios para procesar documentos.</p>}
  </div>;
}

export default function BusinessUnitProviders() {
  const authorized = Number(readStoredUser()?.id) === 1;
  const [units, setUnits] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [saved, setSaved] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [clearApiKey, setClearApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState("");
  const [checks, setChecks] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reload, setReload] = useState(0);
  const requestGeneration = useRef(0);
  const busy = saving || Boolean(checking);
  const dirty = Boolean(config && (JSON.stringify(config) !== JSON.stringify(saved) || apiKey || clearApiKey));
  const selectedUnit = units.find((unit) => String(unit.id) === selectedId);
  const filteredUnits = useMemo(() => {
    const terms = normalizeSearch(query).trim().split(/\s+/).filter(Boolean);
    return units.filter((unit) => terms.every((term) => normalizeSearch(`${unit.name} ${unit.id}`).includes(term)));
  }, [units, query]);

  useEffect(() => {
    if (!authorized) return undefined;
    let active = true;
    setListLoading(true);
    axios.get(`${API}/business-units`).then(({ data }) => {
      if (active) setUnits(Array.isArray(data?.business_units) ? data.business_units : []);
    }).catch((failure) => {
      if (active) setError(errorMessage(failure, "No se pudo cargar la lista de BUs. Vuelve a intentarlo."));
    }).finally(() => { if (active) setListLoading(false); });
    return () => { active = false; };
  }, [authorized, reload]);

  useEffect(() => {
    if (!authorized || !selectedId) return undefined;
    const generation = ++requestGeneration.current;
    setLoading(true);
    setConfig(null);
    setSaved(null);
    setError("");
    setNotice("");
    setChecks({});
    setApiKey("");
    setClearApiKey(false);
    axios.get(`${API}/${selectedId}`).then(({ data }) => {
      if (requestGeneration.current !== generation) return;
      const next = readConfiguration(data);
      setConfig(next);
      setSaved(next);
    }).catch((failure) => {
      if (requestGeneration.current === generation) setError(errorMessage(failure, "No se pudo cargar la configuración de esta BU."));
    }).finally(() => { if (requestGeneration.current === generation) setLoading(false); });
    return () => { requestGeneration.current += 1; };
  }, [authorized, selectedId, reload]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const chooseUnit = (id) => {
    if (busy) return;
    if (String(id) !== selectedId && dirty && !window.confirm("Hay cambios sin guardar. ¿Descartarlos y cambiar de BU?")) return;
    if (String(id) !== selectedId) {
      requestGeneration.current += 1;
      setApiKey("");
      setClearApiKey(false);
      setSelectedId(String(id));
    }
    setPickerOpen(false);
    setQuery("");
  };

  const edit = (provider, key, value) => {
    setConfig((current) => ({ ...current, [provider]: { ...current[provider], [key]: value } }));
    setChecks({});
    setNotice("");
    setError("");
  };

  const save = async (event) => {
    event.preventDefault();
    if (busy || loading || !config) return;
    const generation = requestGeneration.current;
    const aws = Object.fromEntries(Object.entries(config.aws).map(([key, value]) => [key, value.trim()]));
    const openai = { mode: config.openai.mode, project_id: config.openai.project_id.trim() };
    const hasKeyAfterSave = Boolean(apiKey.trim()) || (config.openai.has_key && !clearApiKey);
    if (aws.mode === "business_unit" && ["account_id", "region", "role_arn", "bucket"].some((key) => !aws[key])) {
      setError("Para preparar AWS por BU, completa la cuenta, la región, el rol y el bucket.");
      return;
    }
    if (openai.mode === "business_unit" && (!openai.project_id || !hasKeyAfterSave)) {
      setError("Para preparar OpenAI por BU, indica su proyecto y una clave API de ese proyecto.");
      return;
    }
    if (apiKey.trim()) openai.api_key = apiKey.trim();
    if (clearApiKey) openai.clear_api_key = true;
    setSaving(true);
    setError("");
    setNotice("");
    setChecks({});
    try {
      const { data } = await axios.put(`${API}/${selectedId}`, { aws, openai });
      if (requestGeneration.current !== generation) return;
      const next = readConfiguration(data);
      setConfig(next);
      setSaved(next);
      setApiKey("");
      setClearApiKey(false);
      setNotice(next.routing_enabled ? "Configuración guardada. La vía elegida se aplicará a los nuevos consumos de los flujos habilitados." : "Configuración guardada en preparación. El flujo centralizado continúa funcionando.");
    } catch (failure) {
      if (requestGeneration.current === generation) setError(errorMessage(failure, "No se pudo guardar. Los cambios siguen en el formulario; vuelve a intentarlo."));
    } finally {
      if (requestGeneration.current === generation) setSaving(false);
    }
  };

  const check = async (provider) => {
    if (busy || dirty || !config) return;
    const generation = requestGeneration.current;
    setChecking(provider);
    setChecks((previous) => ({ ...previous, [provider]: null }));
    try {
      const { data } = await axios.post(`${API}/${selectedId}/check/${provider}`);
      if (requestGeneration.current !== generation) return;
      setChecks((previous) => ({ ...previous, [provider]: data?.success === true
        ? { success: true, configuration: data.configuration, checks: data.checks, limitations: data.limitations }
        : { success: false, message: "La conexión no ha podido verificarse. Revisa los accesos guardados." } }));
    } catch (failure) {
      if (requestGeneration.current === generation) setChecks((previous) => ({ ...previous, [provider]: {
        success: false,
        message: errorMessage(failure, "Revisa la configuración guardada y los permisos en el proveedor. Esta comprobación no ha modificado la vía de consumo."),
      } }));
    } finally {
      if (requestGeneration.current === generation) setChecking("");
    }
  };

  if (!authorized) return <div className="buProvidersPage"><p role="alert">Solo el administrador principal puede configurar los servicios por BU.</p></div>;

  return <div className="buProvidersPage">
    <header className="buProvidersHeader">
      <div><span className="buProvidersKicker">Integraciones</span><h1>Servicios por BU</h1><p>Configura dónde se registra el consumo de AWS y OpenAI de cada business unit.</p></div>
      <FiCloud size={32} aria-hidden="true" />
    </header>
    <section className="buProvidersSelect">
      <div><strong>Business unit</strong><p>Los accesos se guardan de forma independiente para cada BU.</p></div>
      <button type="button" className="buProvidersButton" disabled={busy || listLoading} onClick={() => setPickerOpen(true)}><FiSearch aria-hidden="true" />{listLoading ? "Cargando BUs…" : selectedUnit ? `${selectedUnit.name} · BU ${selectedUnit.id}` : "Elegir business unit"}</button>
    </section>
    {error && <div className="buProvidersAlert isError" role="alert">{error}{!config && !loading && <button type="button" className="buProvidersButton" onClick={() => setReload((value) => value + 1)}>Volver a cargar</button>}</div>}
    {notice && <div className="buProvidersAlert isSuccess" role="status"><FiCheckCircle aria-hidden="true" />{notice}</div>}
    {loading && <p role="status">Cargando configuración…</p>}
    {!selectedId && !listLoading && <p className="buProvidersEmpty">{units.length ? "Selecciona una BU para preparar sus accesos." : "No hay business units disponibles."}</p>}
    {config && !loading && <>
      <div className="buProvidersAlert" role="status"><FiShield aria-hidden="true" /><div><strong>{config.routing_enabled ? "Enrutamiento por BU habilitado" : "Modo preparación · flujo centralizado activo"}</strong><p>{config.routing_enabled ? "Cada servicio utiliza la vía guardada en los flujos habilitados. Los documentos existentes conservan su ubicación original." : "Puedes guardar y comprobar los accesos de cada BU. La activación global está deshabilitada: guardar aquí no cambia el flujo centralizado actual."}</p></div></div>
      <form onSubmit={save} autoComplete="off">
        <fieldset className="buProvidersForm" disabled={busy}>
          <section className="buProvidersCard" aria-labelledby="bu-aws-title">
            <div className="buProvidersCardHeader"><h2 id="bu-aws-title">AWS · S3 y Textract</h2><span>{config.aws.mode === "business_unit" ? "Configuración por BU" : "Configuración central"}</span></div>
            <ProviderMode provider="AWS" value={config.aws.mode} onChange={(value) => edit("aws", "mode", value)} routingEnabled={config.routing_enabled} />
            <div className="buProvidersGrid">
              <label>Cuenta AWS (12 dígitos)<input id="bu-aws-account" value={config.aws.account_id} onChange={(event) => edit("aws", "account_id", event.target.value)} inputMode="numeric" maxLength={12} placeholder="123456789012" /></label>
              <label>Región AWS<input id="bu-aws-region" value={config.aws.region} onChange={(event) => edit("aws", "region", event.target.value)} placeholder="eu-west-3" /></label>
              <label className="buProvidersWide">ARN del rol de acceso<input id="bu-aws-role" value={config.aws.role_arn} onChange={(event) => edit("aws", "role_arn", event.target.value)} placeholder="arn:aws:iam::123456789012:role/PilcoreBusinessUnit" /></label>
              <label>External ID (si el rol lo exige)<input id="bu-aws-external" value={config.aws.external_id} onChange={(event) => edit("aws", "external_id", event.target.value)} /></label>
              <label>Nombre del bucket S3<input id="bu-aws-bucket" value={config.aws.bucket} onChange={(event) => edit("aws", "bucket", event.target.value)} placeholder="pilcore-bu-18" /></label>
              <label className="buProvidersWide">Clave KMS (opcional)<input id="bu-aws-kms" value={config.aws.kms_key_id} onChange={(event) => edit("aws", "kms_key_id", event.target.value)} placeholder="ARN o identificador de la clave" /></label>
              <label className="buProvidersWide">URL de entrega de imágenes y medios<input id="bu-aws-public" type="url" value={config.aws.public_base_url} onChange={(event) => edit("aws", "public_base_url", event.target.value)} placeholder="https://imagenes.example.com" /><small>Necesaria para imágenes públicas de booking, onboarding y POS, y para medios de CRM que ya se sirven por URL. Los originales OCR y los logos de empresa siguen privados.</small></label>
            </div>
            <p className="buProvidersHint">Pilcore asume el rol de la cuenta de la BU. No introduzcas claves de acceso AWS individuales.</p>
            <button type="button" className="buProvidersButton" disabled={dirty || busy || !config.aws.role_arn || !config.aws.bucket} onClick={() => check("aws")}>{checking === "aws" ? "Comprobando AWS…" : "Comprobar AWS guardado"}</button>
            <CheckResult provider="AWS" result={checks.aws} />
          </section>
          <section className="buProvidersCard" aria-labelledby="bu-openai-title">
            <div className="buProvidersCardHeader"><h2 id="bu-openai-title">OpenAI</h2><span>{config.openai.mode === "business_unit" ? "Configuración por BU" : "Configuración central"}</span></div>
            <ProviderMode provider="OpenAI" value={config.openai.mode} onChange={(value) => edit("openai", "mode", value)} routingEnabled={config.routing_enabled} />
            <div className="buProvidersGrid">
              <label className="buProvidersWide">ID del proyecto OpenAI<input id="bu-openai-project" value={config.openai.project_id} onChange={(event) => edit("openai", "project_id", event.target.value)} placeholder="proj_…" /></label>
              <label className="buProvidersWide">{config.openai.has_key ? "Nueva clave API (opcional)" : "Clave API del proyecto"}<input id="bu-openai-key" type="password" autoComplete="new-password" spellCheck={false} value={apiKey} disabled={clearApiKey} onChange={(event) => { setApiKey(event.target.value); setChecks({}); setNotice(""); }} placeholder={config.openai.has_key ? "Deja vacío para conservar la clave guardada" : "Pega la clave del proyecto de esta BU"} /><small>{config.openai.has_key ? "Hay una clave guardada. Por seguridad no se vuelve a mostrar." : "Todavía no hay una clave guardada para esta BU."}</small></label>
            </div>
            {config.openai.has_key && <label className="buProvidersRemove"><input type="checkbox" checked={clearApiKey} onChange={(event) => { setClearApiKey(event.target.checked); setApiKey(""); setChecks({}); setNotice(""); }} />Eliminar la clave guardada al guardar</label>}
            {clearApiKey && <p className="buProvidersHint">Para eliminarla, selecciona la vía centralizada. OpenAI por BU necesita una clave válida.</p>}
            <button type="button" className="buProvidersButton" disabled={dirty || busy || !config.openai.has_key || !config.openai.project_id} onClick={() => check("openai")}>{checking === "openai" ? "Comprobando OpenAI…" : "Comprobar OpenAI guardado"}</button>
            <CheckResult provider="OpenAI" result={checks.openai} />
          </section>
        </fieldset>
        <div className="buProvidersActions"><p>{dirty ? "Hay cambios sin guardar. Guarda antes de comprobar los accesos." : "Las comprobaciones utilizan la configuración guardada."}</p><button className="buProvidersButton isPrimary" type="submit" disabled={busy || !dirty}>{saving ? "Guardando…" : "Guardar configuración"}</button></div>
      </form>
    </>}
    <details className="buProvidersSetup"><summary>Qué preparar en cada proveedor</summary>
      <ol><li><strong>AWS:</strong> crea una cuenta miembro por BU y un rol que permita el acceso desde Pilcore. Copia su ID de cuenta y ARN del rol.</li><li><strong>S3:</strong> crea el bucket en esa cuenta, define su región y da acceso al rol. Añade los permisos de Textract y, si procede, KMS.</li><li><strong>OpenAI:</strong> crea un proyecto por BU y una clave propia de ese proyecto. Copia el ID del proyecto y la clave.</li><li><strong>Pilcore:</strong> selecciona la BU, guarda sus accesos y compruébalos. La activación global se realiza por separado tras verificar los flujos.</li></ol>
      <p>Consulta los costes en AWS Billing → Cost Explorer por cuenta vinculada y servicio, y en OpenAI → Usage por proyecto. Esta pantalla configura los accesos; no requiere introducir consumos ni importes.</p>
    </details>
    <Modal show={pickerOpen} onHide={() => { setPickerOpen(false); setQuery(""); }} centered dialogClassName="buProvidersPicker" contentClassName="buProvidersPickerContent" aria-labelledby="bu-picker-title">
      <Modal.Header closeButton closeVariant="white"><Modal.Title id="bu-picker-title">Elegir business unit</Modal.Title></Modal.Header>
      <Modal.Body><label className="buProvidersSearch">Buscar por nombre o ID<input autoFocus type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nombre de la BU…" /></label><div className="buProvidersOptions">{filteredUnits.slice(0, 60).map((unit) => <button type="button" key={unit.id} className={String(unit.id) === selectedId ? "isSelected" : ""} onClick={() => chooseUnit(unit.id)}><strong>{unit.name}</strong><small>BU {unit.id}</small></button>)}</div>{!filteredUnits.length && <p role="status">No hay BUs que coincidan con la búsqueda.</p>}{filteredUnits.length > 60 && <p>Se muestran 60 de {filteredUnits.length}. Afina la búsqueda.</p>}</Modal.Body>
    </Modal>
  </div>;
}
